import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

// Lot 3 impersonation-mail — modal motif obligatoire pour POST
// /admin/impersonate/{user_id}. Le backend (Lot 1, ImpersonateRequest)
// impose reason min 10 chars max 500 → validation UI STRICTEMENT alignée
// pour éviter un 422 après submit.
//
// Décision : modal dédié (pas d'extension de useConfirm/ConfirmDialog).
// Le contrat useConfirm() = boolean, garder universel. La validation
// métier (min 10) reste locale au domaine impersonation.

const MIN_REASON = 10;
const MAX_REASON = 500;

const ImpersonationReasonModal = ({
  isOpen,
  onClose,
  onConfirm,
  targetName,
  isDark = false,
  t,
}) => {
  const dialogRef = useRef(null);
  const textareaRef = useRef(null);
  const [reason, setReason] = useState('');

  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON && trimmed.length <= MAX_REASON;

  // Reset du texte à chaque ouverture (pas de fuite d'un précédent motif).
  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  // A11y : focus trap + ESC ferme, calqué sur ConfirmDialog.js:25-58.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    textareaRef.current?.focus();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = dialogRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!isValid) return;
    onConfirm(trimmed);
  };

  const title = t('impersonation.reason.title');
  const message = t('impersonation.reason.message').replace('{name}', targetName || '');
  const label = t('impersonation.reason.label');
  const placeholder = t('impersonation.reason.placeholder');
  const helperOk = t('impersonation.reason.helperOk').replace('{n}', trimmed.length);
  const helperTooShort = t('impersonation.reason.tooShort')
    .replace('{min}', MIN_REASON)
    .replace('{n}', trimmed.length);
  const helperTooLong = t('impersonation.reason.tooLong').replace('{max}', MAX_REASON);
  const confirmText = t('impersonation.reason.confirm');
  const cancelText = t('common.cancel');
  const closeText = t('common.close');

  let helperNode = helperOk;
  let helperCls = isDark ? 'text-slate-400' : 'text-gray-500';
  if (trimmed.length < MIN_REASON) {
    helperNode = helperTooShort;
    helperCls = isDark ? 'text-amber-400' : 'text-amber-600';
  } else if (trimmed.length > MAX_REASON) {
    helperNode = helperTooLong;
    helperCls = isDark ? 'text-red-400' : 'text-red-600';
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        <motion.div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-md rounded-2xl shadow-2xl outline-none ${
            isDark ? 'bg-slate-800' : 'bg-white'
          } p-6`}
        >
          <button
            onClick={onClose}
            aria-label={closeText}
            className={`absolute top-4 right-4 p-1 rounded-lg transition-colors ${
              isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-400'
            }`}
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className={`w-16 h-16 rounded-full ${isDark ? 'bg-red-500/20' : 'bg-red-100'} flex items-center justify-center mb-4`}>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {title}
            </h3>

            <p className={`mb-4 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
              {message}
            </p>

            <div className="w-full text-left mb-2">
              <label
                htmlFor="impersonation-reason"
                className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
              >
                {label} *
              </label>
              <textarea
                id="impersonation-reason"
                ref={textareaRef}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={placeholder}
                rows={3}
                maxLength={MAX_REASON + 20}
                className={`w-full px-4 py-3 rounded-xl border resize-y ${
                  isDark
                    ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
                    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                }`}
              />
              <p className={`text-xs mt-1 ${helperCls}`} data-testid="reason-helper">
                {helperNode}
              </p>
            </div>

            <div className="flex gap-3 w-full mt-4">
              <button
                onClick={onClose}
                className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                  isDark
                    ? 'bg-slate-700 hover:bg-slate-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {cancelText}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {confirmText}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImpersonationReasonModal;
